import { useEffect, useState } from 'react';
import therapyPlanningService from 'src/services/apiServices/therapyPlanning/therapyPlanning';
import type { ProgressNoteDue } from 'src/types/progressNoteDue';

/**
 * THIN-8144: the patient's open progress-note due dates per discipline, earliest first. A discipline
 * can have several open at once (THIN-8192). Docs due recalculates on planner save, so it refetches
 * whenever the planner data reloads or a cell edit lands.
 */
const useProgressNoteDue = (
  patientId: string | undefined,
  plannerData: unknown,
  cellEdited: unknown
) => {
  const [dueByDiscipline, setDueByDiscipline] = useState<
    Partial<Record<string, ProgressNoteDue[]>>
  >({});

  useEffect(() => {
    if (!patientId) {
      setDueByDiscipline({});
      return;
    }
    let cancelled = false;
    therapyPlanningService
      .getProgressNoteDue(patientId)
      .then((response: any) => {
        if (cancelled) return;
        const list: ProgressNoteDue[] = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
        const grouped: Partial<Record<string, ProgressNoteDue[]>> = {};
        list.forEach((due) => {
          grouped[due.discipline] = [...(grouped[due.discipline] ?? []), due];
        });
        setDueByDiscipline(grouped);
      })
      .catch(() => {
        // The indicator is advisory; a failed read leaves the planner as it was.
        if (!cancelled) setDueByDiscipline({});
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, plannerData, cellEdited]);

  return dueByDiscipline;
};

export default useProgressNoteDue;
